import { Controller } from "@hotwired/stimulus"

const NAME_DEBOUNCE_MS = 600
const SPINNER_CLASSES = { fa: "fa fa-spinner fa-spin", ph: "ph ph-circle-notch ph-spin" }

// Billing: practitioners rarely know their SIRET. While the field is blank,
// asks the server for the establishments the company register lists under the
// practitioner's name ("Profil utilisateur" fields, else the saved name) and
// streams them into the `list` target. Using one fills the SIRET, which runs
// the `form--company-lookup` answer as if it was typed. While the register is
// asked, the list shows a spinner and `searching` (its `%{name}` interpolated).
// Asked when the name changes and when the blank SIRET gets focus. On connect
// only with `suggestOnConnect` (the practitioner's billing page, the admin
// creation form, whose name a training contact may prefill: one form each):
// the admin index renders one edit form per practitioner.
export default class extends Controller {
  static targets = ["siret", "list"]
  static values = {
    url: String,
    firstNameField: String,
    lastNameField: String,
    firstName: String,
    lastName: String,
    searching: String,
    suggestOnConnect: Boolean
  }

  connect () {
    this.form = this.element.closest("form") || this.element
    this.nameChanged = this.nameChanged.bind(this)
    this.form.addEventListener("input", this.nameChanged)
    if (this.suggestOnConnectValue) this.suggest()
  }

  disconnect () {
    this.form.removeEventListener("input", this.nameChanged)
    clearTimeout(this.timeout)
  }

  nameChanged (event) {
    if (![this.firstNameFieldValue, this.lastNameFieldValue].includes(event.target.id)) return

    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => this.suggest(), NAME_DEBOUNCE_MS)
  }

  async suggest () {
    if (this.siretTarget.value.trim()) return

    const firstName = this.read(this.firstNameFieldValue, this.firstNameValue)
    const lastName = this.read(this.lastNameFieldValue, this.lastNameValue)
    if (!firstName || !lastName) return this.clear()

    const query = new URLSearchParams({ first_name: firstName, last_name: lastName, target: this.listTarget.id }).toString()
    if (query === this.lastQuery) return

    this.lastQuery = query
    this.showSearching(`${firstName} ${lastName}`)
    try {
      const response = await fetch(`${this.urlValue}?${query}`, { headers: { Accept: "text/vnd.turbo-stream.html" } })
      if (!response.ok) return this.forget(query)

      const stream = await response.text()
      // Too late: a SIRET was entered or the name changed while the register answered.
      if (this.siretTarget.value.trim() || this.lastQuery !== query) return

      window.Turbo.renderStreamMessage(stream)
    } catch {
      this.forget(query)
    }
  }

  showSearching (name) {
    const line = document.createElement("p")
    line.className = "practitioner-siret-suggestions__line text-muted mb-0"
    line.dataset.searching = ""

    // The admin keeps Font Awesome (`data-icon-set` on <html>); other layouts use Phosphor.
    const spinner = document.createElement("i")
    spinner.className = SPINNER_CLASSES[document.documentElement.dataset.iconSet === "fa" ? "fa" : "ph"]
    spinner.setAttribute("aria-hidden", "true")

    const text = document.createElement("span")
    text.textContent = this.searchingValue.replace("%{name}", name)

    line.append(spinner, text)
    this.listTarget.replaceChildren(line)
  }

  // Lets the same name be asked again after a failed request.
  forget (query) {
    if (this.lastQuery !== query) return

    this.lastQuery = null
    if (this.listTarget.querySelector("[data-searching]")) this.listTarget.replaceChildren()
  }

  use ({ params: { siret } }) {
    this.siretTarget.value = siret
    this.siretTarget.dispatchEvent(new Event("input", { bubbles: true }))
  }

  // A SIRET in the field, typed or used, takes the suggestions' place.
  siretChanged () {
    if (this.siretTarget.value.trim()) this.clear()
  }

  clear () {
    this.lastQuery = null
    this.listTarget.replaceChildren()
  }

  // Form fields win over the saved values: they carry what the admin is typing.
  read (fieldId, fallback) {
    const field = this.form.querySelector(`[id="${fieldId}"]`)
    return field?.value?.trim() || fallback
  }
}
